// LotWise Auto — anonymous funnel analytics.
// Session id lives in localStorage; there's no PII beyond what the
// caller chooses to attach to `properties`. Failures are swallowed
// so a network hiccup never impacts the user flow.

import { api } from "./api";

const SESSION_KEY = "lw-session-id";

function makeId() {
  // Short, url-safe, high-entropy enough for anonymous session tagging.
  return (
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

export function getSessionId() {
  if (typeof window === "undefined") return "server";
  try {
    let sid = window.localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = makeId();
      window.localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Private mode or storage blocked — fall back to an in-memory id.
    if (!window.__lwSid) window.__lwSid = makeId();
    return window.__lwSid;
  }
}

// Emitted event vocabulary (kept in sync with backend Literal).
export const EVENTS = {
  QUALIFIER_STARTED: "qualifier_started",
  QUALIFIER_COMPLETED: "qualifier_completed",
  BRIEF_OPENED: "brief_opened",
  BRIEF_EMAILED: "brief_emailed",
  BRIEF_PRINTED: "brief_printed",
  REQUEST_VISIT_STARTED: "request_visit_started",
  REQUEST_VISIT_COMPLETED: "request_visit_completed",
};

// Fires one anonymous funnel event. Never blocks or throws to callers.
//
// Note on dedupe: this helper fires every time it is called. Callers
// that dispatch from React effects should guard with a `useRef` flag
// so React 18 StrictMode's double-effect in dev does not double-count.
// Callers that dispatch from click handlers are naturally single-shot
// and require no additional guard.
export function track(eventType, properties) {
  return api
    .post("/events", {
      event_type: eventType,
      session_id: getSessionId(),
      properties: properties || null,
    })
    .catch(() => {
      // Analytics must never break the app.
    });
}

// Alias kept for clarity at call sites that intentionally emit every
// time (e.g., a completed lead submission). Behaviour is identical to
// track() now that per-page dedupe lives at the effect layer.
export const trackAlways = track;
